<template>
  <div :class="cn(props.class)">
    <PensionPortfolioProductCategories v-model="selectedCategory" :policies="flatData" />

    <ClientOnly>
      <template #fallback>
        <UiCard class="min-h-xxl grid place-content-center"> <UiSpinner size="s" /> </UiCard>
      </template>

      <div class="mt-5" v-auto-animate>
        <div class="flex flex-col gap-6" v-if="isMobile">
          <PensionPortfolioPolicyCard
            v-for="(policy, index) in flatData"
            @click="handlePolicyDrawer(policy)"
            :policy
            :key="index"
          />
        </div>

        <div class="rounded-md border" v-else>
          <UiTableFetcher
            :loading-skeletons="2"
            :classes="{
              body: {
                row: cn(
                  'last-of-type:border-b-none cursor-pointer data-[state=selected]:bg-white data-[state=selected]:shadow-lg data-[state=selected]:shadow-black/5'
                ),
              },
            }"
            :query
            :columns
            @row-click="(row) => row.toggleExpanded()"
          >
            <!-- @row-mouse-enter="(row) => prefetchComponents('PensionPortfolioOverviewTypesEducation')" -->
            <template #expandableRow="{ row }">
              <PensionPortfolioScreen :policy="row?.original || {}" :category="selectedCategory" />
            </template>
          </UiTableFetcher>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="tsx">
import { PensionPortfolioPolicyDrawer, UiInfo, UiTableFetcher } from "#components"
import { createColumnHelper } from "@tanstack/vue-table"

import { MyPensionPortfolioPolicy, PensionCategoriesEnum } from "@finq/pension-portfolio/types/portfolio"

const props = defineProps<{ class?: ClassValue }>()

const { t } = useI18n({ useScope: "local" })
const { isMobile } = useDisplay()
const popup = usePopup()

const selectedCategory = ref<PensionCategoriesEnum | undefined>(PensionCategoriesEnum.PENSION)
const query = useMyPensionPortfolioPolicies(selectedCategory)

const flatData = computed(
  () => query.data.value?.pages?.flatMap((data) => data.data as MyPensionPortfolioPolicy[]) || []
)

const handlePolicyDrawer = (policy: MyPensionPortfolioPolicy) => {
  popup.open({
    id: `pension-portfolio-policy-drawer-${policy.fundId}`,
    component: () => <PensionPortfolioPolicyDrawer policy={policy} category={selectedCategory.value} />,
    classes: {
      toolbar: "mb-0 hidden",
      modal: {
        panel: cn("h-full max-h-dvh w-full max-w-none overflow-auto rounded-none p-0"),
      },
    },
    dialogProps: { contentBinds: { hideCloseButton: true } },
    scrollArea: false,
  })
}

// Make sure to close the drawer when switching from mobile to desktop
watch(isMobile, () => {
  if (!popup.messages.value.length) return
  for (const popupMsg of popup.messages.value) {
    if (!popupMsg.id?.includes("pension-portfolio-policy-drawer")) continue
    popupMsg.destroy?.()
  }
})

const columnHelper = createColumnHelper<MyPensionPortfolioPolicy>()

const columns = computed(() => {
  const textHelpers = usePensionPortfolioTextHelpers()

  const columns = []

  columns.push(
    columnHelper.accessor("id", {
      header: (cell) => {
        return <span class="block w-14" />
      },
      cell: (cell) => {
        // return cell.row.original.recommendationId ? (
        //   <PensionPortfolioPartialsRecommendationChip class="w-14" />
        // ) : (
        ;<span class="block w-14" />
        // )
      },
      meta: { flex: "0 0 72px", classes: { cell: "whitespace-pre pe-0", headerCell: "pe-0" } },
    }),
    columnHelper.accessor("companyId", {
      header: t("row.companyId"),
      cell: (cell) => {
        const policyTitle = computed(() => {
          if (selectedCategory.value === PensionCategoriesEnum.INSURANCE) {
            return `${cell.row.original?.companyShortText} - ${cell.row.original?.planName}`
          }

          if (cell.row.original?.investmentPlans?.length > 1) {
            // e.g. 'קרן פנסיה - 3 מסלולי השקעה'
            return `${cell.row.original?.companyShortText} ${textHelpers.extractFrom(PensionCategories, selectedCategory.value)} - ${t("investmentPlans", { count: cell.row.original.investmentPlans.length })}`
          }

          return cell.row.original?.fundShortText?.toUpperCase()
        })

        return (
          <UiInfo
            clamp
            image={cell.row.original.companyLogo}
            has-image
            image-size="m"
            title={policyTitle.value}
            subtitle={cell.row.original?.policyNumberId?.replace("-", "")}
            classes={{ image: "border" }}
          />
        )
      },
      meta: { flex: "1", classes: { cell: "whitespace-pre" } },
    })
  )

  if (selectedCategory.value !== PensionCategoriesEnum.INSURANCE) {
    columns.push(
      columnHelper.accessor("currentSavings", {
        header: t("row.currentSavings"),
        cell: (cell) => toCash(cell.row.original.currentSavings, NIS),
        meta: { flex: "0 0 10%", classes: { cell: "whitespace-pre" } },
      })
    )
  }

  columns.push(
    columnHelper.accessor("joinDate", {
      header: t("row.joinDate"),
      cell: (cell) => textHelpers.formatDate(cell.row.original.joinDate),
      meta: { flex: "0 0 12%", classes: { cell: "whitespace-pre" } },
    }),

    columnHelper.accessor("status", {
      header: t("row.status"),
      cell: (cell) => textHelpers.toStatus(cell.getValue()),
      meta: { flex: "0 0 10%", classes: { cell: "whitespace-pre" } },
    }),

    columnHelper.accessor("posStand", {
      header: t("row.posStand"),
      cell: (cell) => textHelpers.toExists(textHelpers.extractFrom(PosStandTitles, cell.getValue()), true),
      meta: { flex: "0 0 20%", classes: { cell: "whitespace-pre" } },
    })
  )

  return columns
})
</script>

<i18n lang="json">
{
  "he": {
    "row": {
      "companyId": "שם ומספר פוליסה",
      "currentSavings": "חיסכון נוכחי",
      "joinDate": "תאריך הצטרפות",
      "status": "סטטוס",
      "posStand": "מעמד"
    },
    "categories": {
      "PENSION": "קרן פנסיה",
      "EDUCATION": "קרן השתלמות",
      "INVESTMENT": "גמל להשקעה",
      "MANAGERS": "ביטוח מנהלים",
      "CHILDSAVING": "חיסכון לכל ילד",
      "PROVIDENT": "קופת תגמולים",
      "INSURANCE": "כיסויים ביטוחיים"
    },
    "ACTIVE": "פעיל",
    "INACTIVE": "לא פעיל",
    "investmentPlans": "{count} מסלולי השקעה"
  },
  "en": {}
}
</i18n>
